/**
 * Stock Batches Page - Manage product lots and expiry dates
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../../lib/api';
import { useInstance } from '../../../contexts/InstanceContext';

import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { DataGrid } from '../../../components/ui/DataGrid';
import PageHeader from '../../../components/ui/PageHeader';
import { Modal } from '../../../components/ui/modal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { Plus, Package, Edit, Trash2, AlertTriangle, Calendar, Eye } from 'lucide-react';

import './styles.css';

export default function Batches() {
  const { t } = useTranslation();
  const { formatCurrency, formatDate } = useInstance();
  
  const [batches, setBatches] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState('expiry_date');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filters, setFilters] = useState({});
  
  const [modalOpen, setModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [editingBatch, setEditingBatch] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  
  const [formData, setFormData] = useState({
    product_id: '',
    batch_number: '',
    quantity: '',
    expiry_date: '',
    manufacturing_date: '',
    cost_price: '',
    supplier: '',
    notes: '',
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load products for filter dropdown
      const productsRes = await api.get('/products?active_only=true');
      setProducts(productsRes.data);
      
      // Load batches with pagination
      const batchesRes = await api.post('/batches/list', {
        pageNumber,
        pageSize,
        sortBy,
        sortDirection,
        filters: {
          product_id: filters.product_id || null,
          status: filters.status || null,
          expiring_within_days: filters.expiring_soon ? 7 : null,
        },
      });
      
      if (batchesRes.data.success) {
        setBatches(batchesRes.data.data.items);
        setTotalItems(batchesRes.data.data.totalItems);
      }
    } catch (err) {
      toast.error('Erro ao carregar lotes');
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageSize, sortBy, sortDirection, filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        quantity: parseInt(formData.quantity) || 0,
        cost_price: parseFloat(formData.cost_price) || 0,
      };
      
      if (editingBatch) {
        await api.put(`/batches/${editingBatch.id}`, data);
        toast.success('Lote atualizado com sucesso!');
      } else {
        await api.post('/batches', data);
        toast.success('Lote criado com sucesso!');
      }
      
      setModalOpen(false);
      resetForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar lote');
    }
  };

  // Handle modal close - reset form when closing
  const handleModalOpenChange = (open) => {
    setModalOpen(open);
    if (!open) {
      setTimeout(() => resetForm(), 100);
    }
  };

  const handleEdit = (batch) => {
    setEditingBatch(batch);
    setFormData({
      product_id: batch.product_id,
      batch_number: batch.batch_number,
      quantity: batch.quantity.toString(),
      expiry_date: batch.expiry_date || '',
      manufacturing_date: batch.manufacturing_date || '',
      cost_price: batch.cost_price?.toString() || '',
      supplier: batch.supplier || '',
      notes: batch.notes || '',
    });
    setModalOpen(true);
  };

  const handleViewDetails = (batch) => {
    setSelectedBatch(batch);
    setDetailsModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/batches/${deleteId}`);
      toast.success('Lote excluído com sucesso!');
      loadData();
    } catch (err) {
      toast.error('Erro ao excluir lote');
    } finally {
      setDeleteId(null);
    }
  };

  const resetForm = () => {
    setEditingBatch(null);
    setFormData({
      product_id: '',
      batch_number: '',
      quantity: '',
      expiry_date: '',
      manufacturing_date: '',
      cost_price: '',
      supplier: '',
      notes: '',
    });
  };

  const columns = useMemo(() => [
    {
      field: 'product_name',
      headerName: 'Produto',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'batch_number',
      headerName: 'Lote',
      width: 120,
    },
    {
      field: 'quantity',
      headerName: 'Quantidade',
      width: 100,
      type: 'number',
    },
    {
      field: 'expiry_date',
      headerName: 'Validade',
      width: 120,
      cellRenderer: (params) => {
        if (!params.value) return '-';
        const isExpired = params.data.is_expired;
        const daysUntil = params.data.days_until_expiry;
        return (
          <div className="flex items-center gap-2">
            <span className={isExpired ? 'text-destructive' : daysUntil <= 7 ? 'text-amber-500' : ''}>
              {formatDate(params.value)}
            </span>
            {isExpired && <Badge variant="destructive">Vencido</Badge>}
            {!isExpired && daysUntil <= 7 && (
              <Badge variant="warning" className="bg-amber-500">{daysUntil}d</Badge>
            )}
          </div>
        );
      },
    },
    {
      field: 'cost_price',
      headerName: 'Custo',
      width: 100,
      valueFormatter: (params) => formatCurrency(params.value || 0),
    },
    {
      field: 'supplier',
      headerName: 'Fornecedor',
      width: 150,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      cellRenderer: (params) => (
        <Badge variant={params.value === 'active' ? 'default' : 'secondary'}>
          {params.value === 'active' ? 'Ativo' : params.value === 'expired' ? 'Vencido' : params.value}
        </Badge>
      ),
    },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 130,
      sortable: false,
      cellRenderer: (params) => (
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={() => handleViewDetails(params.data)} title="Ver detalhes">
            <Eye className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => handleEdit(params.data)} title="Editar">
            <Edit className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setDeleteId(params.data.id)} title="Excluir">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ], [formatCurrency, formatDate]);

  const filterDefinitions = [
    {
      key: 'product_id',
      label: 'Produto',
      type: 'select',
      placeholder: 'Todos os produtos',
      options: [
        { value: '', label: 'Todos os produtos' },
        ...products.map(p => ({ value: p.id, label: p.name })),
      ],
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      placeholder: 'Todos',
      options: [
        { value: '', label: 'Todos' },
        { value: 'active', label: 'Ativo' },
        { value: 'expired', label: 'Vencido' },
        { value: 'consumed', label: 'Consumido' },
      ],
    },
    {
      key: 'expiring_soon',
      label: 'Vencendo em breve',
      type: 'checkbox',
    },
  ];

  return (
    <div className="batches-page" data-testid="batches-page">
      <PageHeader
        icon={Package}
        title="Lotes de Produtos"
        subtitle={`${totalItems} lotes cadastrados`}
        actions={
          <Button onClick={() => { resetForm(); setModalOpen(true); }} data-testid="new-batch-btn">
            <Plus className="h-4 w-4 mr-2" />
            Novo Lote
          </Button>
        }
        filters={filterDefinitions}
        filterValues={filters}
        onFilterChange={(newFilters) => {
          setFilters(newFilters);
          setPageNumber(1);
        }}
        onSearch={() => loadData()}
        onClearFilters={() => {
          setFilters({});
          setPageNumber(1);
        }}
        exportFileName="lotes"
        exportData={batches}
      />

      <DataGrid
        title="Lista de Lotes"
        rowData={batches}
        columnDefs={columns}
        loading={loading}
        pagination={true}
        paginationPageSize={pageSize}
        onPaginationChanged={(params) => {
          const newPage = params.api.paginationGetCurrentPage() + 1;
          if (newPage !== pageNumber) {
            setPageNumber(newPage);
          }
        }}
        onSortChanged={(params) => {
          const sortModel = params.api.getSortModel();
          if (sortModel.length > 0) {
            setSortBy(sortModel[0].colId);
            setSortDirection(sortModel[0].sort);
          }
        }}
      />

      {/* Create/Edit Modal */}
      <Modal
        open={modalOpen}
        onOpenChange={handleModalOpenChange}
        title={editingBatch ? 'Editar Lote' : 'Novo Lote'}
        description={editingBatch ? 'Atualize as informações do lote' : 'Preencha os dados do novo lote'}
        size="default"
        footer={
          <>
            <Button type="button" variant="outline" onClick={() => handleModalOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" form="batch-form" data-testid="save-batch-btn">Salvar</Button>
          </>
        }
      >
        <form id="batch-form" onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Produto *</Label>
            <Select
              value={formData.product_id}
              onValueChange={(v) => setFormData(p => ({ ...p, product_id: v }))}
              disabled={!!editingBatch}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione um produto" />
              </SelectTrigger>
              <SelectContent>
                {products.filter(p => p.has_batch_control || !editingBatch).map(product => (
                  <SelectItem key={product.id} value={product.id}>
                    {product.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Número do Lote *</Label>
              <Input
                value={formData.batch_number}
                onChange={(e) => setFormData(p => ({ ...p, batch_number: e.target.value }))}
                required
                data-testid="batch-number-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Quantidade *</Label>
              <Input
                type="number"
                min="0"
                value={formData.quantity}
                onChange={(e) => setFormData(p => ({ ...p, quantity: e.target.value }))}
                required
                data-testid="batch-quantity-input"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Data de Validade</Label>
              <Input
                type="date"
                value={formData.expiry_date}
                onChange={(e) => setFormData(p => ({ ...p, expiry_date: e.target.value }))}
                data-testid="batch-expiry-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Data de Fabricação</Label>
              <Input
                type="date"
                value={formData.manufacturing_date}
                onChange={(e) => setFormData(p => ({ ...p, manufacturing_date: e.target.value }))}
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Preço de Custo</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.cost_price}
                onChange={(e) => setFormData(p => ({ ...p, cost_price: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Fornecedor</Label>
              <Input
                value={formData.supplier}
                onChange={(e) => setFormData(p => ({ ...p, supplier: e.target.value }))}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Observações</Label>
            <Input
              value={formData.notes}
              onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
            />
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este lote? O estoque do produto será ajustado automaticamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Details Modal */}
      <Modal
        open={detailsModalOpen}
        onOpenChange={setDetailsModalOpen}
        title={
          <span className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Detalhes do Lote
          </span>
        }
        size="default"
        footer={
          <>
            <Button variant="outline" onClick={() => setDetailsModalOpen(false)}>
              Fechar
            </Button>
            <Button onClick={() => { setDetailsModalOpen(false); handleEdit(selectedBatch); }}>
              <Edit className="h-4 w-4 mr-2" />
              Editar
            </Button>
          </>
        }
      >
        {selectedBatch && (
          <div className="space-y-4">
            <div className="p-4 bg-muted rounded-lg">
              <h3 className="font-semibold text-lg">{selectedBatch.product_name}</h3>
              <p className="text-sm text-muted-foreground">Lote: {selectedBatch.batch_number}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Quantidade</p>
                <p className="font-mono font-semibold text-lg">{selectedBatch.quantity}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Preço de Custo</p>
                <p className="font-mono font-semibold text-lg">{formatCurrency(selectedBatch.cost_price || 0)}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Data de Fabricação</p>
                <p className="font-medium">{selectedBatch.manufacturing_date ? formatDate(selectedBatch.manufacturing_date) : '-'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Data de Validade</p>
                <div className="flex items-center gap-2">
                  <p className={`font-medium ${selectedBatch.is_expired ? 'text-destructive' : selectedBatch.days_until_expiry <= 7 ? 'text-amber-500' : ''}`}>
                    {selectedBatch.expiry_date ? formatDate(selectedBatch.expiry_date) : '-'}
                  </p>
                  {selectedBatch.is_expired && <Badge variant="destructive">Vencido</Badge>}
                  {!selectedBatch.is_expired && selectedBatch.days_until_expiry <= 7 && (
                    <Badge className="bg-amber-500">{selectedBatch.days_until_expiry} dias</Badge>
                  )}
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Fornecedor</p>
                <p className="font-medium">{selectedBatch.supplier || '-'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Status</p>
                <Badge variant={selectedBatch.status === 'active' ? 'default' : 'secondary'}>
                  {selectedBatch.status === 'active' ? 'Ativo' : selectedBatch.status === 'expired' ? 'Vencido' : selectedBatch.status}
                </Badge>
              </div>
            </div>
            
            {selectedBatch.notes && (
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Observações</p>
                <p className="text-sm bg-muted p-2 rounded">{selectedBatch.notes}</p>
              </div>
            )}
            
            <div className="text-xs text-muted-foreground pt-2 border-t">
              <p>Criado em: {formatDate(selectedBatch.created_at)}</p>
              {selectedBatch.updated_at && <p>Atualizado em: {formatDate(selectedBatch.updated_at)}</p>}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
